import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import message1 from '../../assets/message1.mp4';
import missionVideo from '../../assets/mission.MOV';
import message3 from '../../assets/message3.mp4';
import message4 from '../../assets/message4.mp4';

const messageVideos = [
  {
    id: 'message-4',
    src: message4,
    label: 'Message Himmat video 4',
  },
  {
    id: 'message-3',
    src: message3,
    label: 'Message Himmat video 3',
  },
  {
    id: 'message-1',
    src: message1,
    label: 'Message Himmat video 1',
  },
  {
    id: 'message-2',
    src: missionVideo,
    label: 'Message Himmat video 2',
  },
  
];

const DESKTOP_VISIBLE = 3;

function useVisibleSlideCount() {
  const [count, setCount] = useState(1);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const sync = () => setCount(media.matches ? DESKTOP_VISIBLE : 1);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return count;
}

const MessageVideoCard = ({ entry, videoRef }) => (
  <article className="mx-auto flex h-full w-full max-w-[280px] flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-lg sm:max-w-[300px]">
    <div className="relative aspect-[9/16] w-full bg-black">
      <video
        ref={videoRef}
        src={entry.src}
        className="absolute inset-0 h-full w-full object-contain"
        controls
        playsInline
        preload="metadata"
        aria-label={entry.label}
      />
    </div>
  </article>
);

const HomeMissionHimmat = () => {
  const visibleCount = useVisibleSlideCount();
  const total = messageVideos.length;
  const maxIndex = Math.max(0, total - visibleCount);
  const [activeIndex, setActiveIndex] = useState(0);
  const videoRefs = useRef([]);

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  useEffect(() => {
    videoRefs.current.forEach((el, index) => {
      if (!el) return;
      const isVisible =
        index >= activeIndex && index < activeIndex + visibleCount;
      if (!isVisible) {
        el.pause();
      }
    });
  }, [activeIndex, visibleCount]);

  const goTo = useCallback(
    (index) => {
      setActiveIndex(Math.max(0, Math.min(index, maxIndex)));
    },
    [maxIndex],
  );

  const goPrev = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  const useCarousel = total > visibleCount;
  const trackWidthPercent = (total / visibleCount) * 100;
  const slideShareOfTrack = 100 / total;

  const centeredSlotClass =
    total === 1
      ? 'w-full max-w-[300px]'
      : total === 2
        ? 'w-full max-w-[300px] lg:max-w-[calc((100%-2.5rem)/2)] lg:flex-[1_1_0]'
        : 'w-full max-w-[300px] lg:max-w-[calc((100%-5rem)/3)] lg:flex-[1_1_0]';

  return (
    <section id="message-himmat" className="bg-white px-6 py-14 md:px-12">
      <div className="mx-auto max-w-6xl">
        <h2 className="mb-10 text-center text-3xl font-bold uppercase text-accent md:mb-12 md:text-4xl">
          Message Himmat
        </h2>

        {useCarousel ? (
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{
                width: `${trackWidthPercent}%`,
                transform: `translateX(-${activeIndex * slideShareOfTrack}%)`,
              }}
            >
              {messageVideos.map((entry, index) => (
                <div
                  key={entry.id}
                  className="box-border flex flex-shrink-0 justify-center px-2 sm:px-3"
                  style={{ width: `${slideShareOfTrack}%` }}
                >
                  <MessageVideoCard
                    entry={entry}
                    videoRef={(el) => {
                      videoRefs.current[index] = el;
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-start justify-center gap-6 lg:gap-10">
            {messageVideos.map((entry, index) => (
              <div key={entry.id} className={centeredSlotClass}>
                <MessageVideoCard
                  entry={entry}
                  videoRef={(el) => {
                    videoRefs.current[index] = el;
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {useCarousel && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <button
              type="button"
              onClick={goPrev}
              disabled={activeIndex === 0}
              className="rounded-full border border-gray-200 bg-white p-2.5 text-primary shadow-sm transition hover:bg-bg-light disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Previous message video"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden />
            </button>

            <div
              className="flex flex-wrap justify-center gap-2"
              role="tablist"
              aria-label="Message Himmat videos"
            >
              {Array.from({ length: maxIndex + 1 }, (_, pageIndex) => (
                <button
                  key={pageIndex}
                  type="button"
                  role="tab"
                  aria-selected={pageIndex === activeIndex}
                  aria-label={`Show message video ${pageIndex + 1}`}
                  onClick={() => goTo(pageIndex)}
                  className={`h-2.5 rounded-full transition-all ${
                    pageIndex === activeIndex
                      ? 'w-8 bg-accent'
                      : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={goNext}
              disabled={activeIndex >= maxIndex}
              className="rounded-full border border-gray-200 bg-white p-2.5 text-primary shadow-sm transition hover:bg-bg-light disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Next message video"
            >
              <ChevronRight className="h-6 w-6" aria-hidden />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default HomeMissionHimmat;
